import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';

import {
  CatalogProductDetailViewFragmentFragment,
  CatalogVariantFragmentFragment,
} from '@notino/shared/definitions/types';

import { useImageAttributes } from '@components/CdnImage/useImageAttributes';

import { useSsrProductUrl } from '../hooks/useSsrProductUrl';

import { replaceMetaInformation } from './utils';

interface HeaderMetaParams {
  product: CatalogProductDetailViewFragmentFragment;
  variant: CatalogVariantFragmentFragment;
}

export const HeadMeta = ({ product, variant }: HeaderMetaParams) => {
  const { url, xDefault } = useSsrProductUrl(product);
  const { pathname } = useLocation();
  const { src: imageSrc } = useImageAttributes(
    variant.media?.mainImage?.src ?? '',
    'metaTag'
  );

  React.useEffect(() => {
    replaceMetaInformation({
      keywords: variant.metaData.keywords ?? '',
      title: variant.metaData.title ?? '',
      description: variant.metaData.description ?? '',
    });
  }, [
    variant.metaData.title,
    variant.metaData.description,
    variant.metaData.keywords,
  ]);

  const variantSite = /(\/p-\d+((\/?)|(\??.+)))$/;

  const showHrefLangs = !variantSite.test(pathname);

  return (
    <Helmet>
      <meta property="og:type" content="product" />
      <meta property="og:url" content={url ?? undefined} />
      <meta
        property="og:description"
        content={variant.metaData.description ?? undefined}
      />
      <meta property="og:title" content={variant.metaData.title ?? undefined} />
      <meta property="og:image" content={imageSrc} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:site_name"
        content={url ? new URL(url).hostname : undefined}
      />
      <link rel="canonical" href={url ?? undefined} />

      {showHrefLangs
        ? product.alternateSites
            ?.filter(Boolean)
            .map((site) => (
              <link
                key={site!.culture}
                rel="alternate"
                hrefLang={site!.culture ?? undefined}
                href={site!.link ?? undefined}
              />
            ))
        : null}
      {showHrefLangs ? (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={xDefault?.link ?? undefined}
        />
      ) : null}
    </Helmet>
  );
};
